import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  X,
  Phone,
  ArrowSquareOut,
  Note,
  PhoneCall,
  EnvelopeSimple,
  ChatText,
  Handshake,
  Tag,
  ClockCounterClockwise,
  CalendarPlus,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";

const STAGE_COLOR = {
  "Lead In": "text-zinc-300",
  Contacted: "text-sky-300",
  Opportunity: "text-violet-300",
  Quoted: "text-amber-300",
  Sent: "text-blue-300",
  Viewed: "text-cyan-300",
  Won: "text-emerald-300",
  Lost: "text-red-300",
};

const Stat = ({ label, value, tone = "text-zinc-100" }) => (
  <div className="whitespace-nowrap">
    <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{label}</span>
    <div className={`font-mono-data text-sm font-bold ${tone}`}>{value}</div>
  </div>
);

const PipelineCard = ({ card, onOpen, onDragStart }) => (
  <button
    type="button"
    draggable
    onDragStart={(e) => onDragStart(e, card)}
    onClick={() => onOpen(card)}
    data-testid={`pipeline-card-${card.entity}-${card.id}`}
    className="group block w-full rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-left transition-colors duration-150 hover:border-indigo-500/60"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="line-clamp-1 text-sm font-semibold text-zinc-100">{card.title}</span>
      <span className="shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-zinc-400">
        {card.kind}
      </span>
    </div>
    {card.subtitle ? (
      <div className="line-clamp-1 text-[11px] text-zinc-500">{card.subtitle}</div>
    ) : null}
    <div className="mt-2 flex items-end justify-between">
      <span className="font-mono-data text-sm font-bold text-zinc-200">{money(card.value)}</span>
      <span className="text-[10px] text-zinc-500">
        {Math.round(card.prob * 100)}% · {card.days_in_stage}d
      </span>
    </div>
  </button>
);

const ACT_TABS = [
  { key: "note", label: "Note", icon: Note },
  { key: "call", label: "Call", icon: PhoneCall },
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "text", label: "Text", icon: ChatText },
  { key: "meeting", label: "Meeting", icon: Handshake },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const Drawer = ({ detail, onClose, onMove, onReload }) => {
  const [tab, setTab] = useState("note");
  const [note, setNote] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const d = detail;

  const addActivity = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/pipeline/${d.entity}/${d.id}/activity`, { type: tab, text: note });
      setNote("");
      onReload();
    } catch {
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };
  const addTag = async (e) => {
    e.preventDefault();
    const t = tagInput.trim();
    if (!t) return;
    const tags = Array.from(new Set([...(d.tags || []), t]));
    setTagInput("");
    await api.put(`/pipeline/${d.entity}/${d.id}/tags`, { tags });
    onReload();
  };
  const removeTag = async (t) => {
    await api.put(`/pipeline/${d.entity}/${d.id}/tags`, {
      tags: (d.tags || []).filter((x) => x !== t),
    });
    onReload();
  };

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[460px] flex-col overflow-y-auto border-l border-zinc-800 bg-zinc-950 shadow-2xl"
      data-testid="pipeline-drawer"
    >
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-4 py-3">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Deal timeline</div>
          <div className="line-clamp-1 text-sm font-bold text-zinc-100">{d.title}</div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={d.link}
            data-testid="pipeline-drawer-open"
            className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
          >
            Open <ArrowSquareOut size={13} weight="bold" />
          </Link>
          <button type="button" onClick={onClose} data-testid="pipeline-drawer-close" className="text-zinc-500 hover:text-zinc-200">
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      <div className="space-y-5 p-4">
        {/* value + appointments */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Deal value</span>
              <div className="font-mono-data text-lg font-bold text-zinc-100">{money(d.value)}</div>
            </div>
            <div className="text-right">
              <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Weighted</span>
              <div className="font-mono-data text-sm font-bold text-zinc-300">{money(d.weighted)}</div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-zinc-800 pt-2">
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Appointments</span>
            <button
              type="button"
              data-testid="pipeline-schedule"
              onClick={() => toast.info("Schedule from the calendar — appointments there attach here automatically")}
              className="flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500"
            >
              <CalendarPlus size={12} weight="bold" /> Schedule
            </button>
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">Nothing booked. Appointments on the shop calendar land here automatically.</p>
        </div>

        {/* stage */}
        <div>
          <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">Stage</div>
          <div className="flex flex-wrap gap-1.5">
            {d.stages.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`pipeline-stage-btn-${s.replace(/\s+/g, "-")}`}
                onClick={() => onMove(d.entity, d.id, s)}
                className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  d.stage === s
                    ? "border-indigo-500 bg-indigo-500/20 text-indigo-200"
                    : "border-zinc-800 text-zinc-400 hover:border-indigo-500/50"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* source / assignee / account / status */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Source</span>
            <div className="text-zinc-200">{d.source || "—"}</div>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Assignee</span>
            <div className="line-clamp-1 text-zinc-200">{d.assignee || "—"}</div>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Account</span>
            <div className="line-clamp-1 text-zinc-200">{d.account || "—"}</div>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Status</span>
            <div className={STAGE_COLOR[d.stage] || "text-zinc-200"}>{d.stage}</div>
          </div>
        </div>

        {/* contact */}
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
            <Phone size={11} weight="bold" /> Contact
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-xs">
            <div className="font-semibold text-zinc-100">{d.contact?.name || d.title}</div>
            {d.contact?.company ? <div className="text-zinc-400">{d.contact.company}</div> : null}
            {d.contact?.address ? <div className="text-zinc-500">{d.contact.address}</div> : null}
            <div className="mt-2 flex flex-wrap gap-2">
              {d.contact?.phone ? (
                <a href={`tel:${d.contact.phone}`} data-testid="pipeline-call" className="rounded-full border border-emerald-500/40 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                  Call {d.contact.phone}
                </a>
              ) : (
                <span className="rounded-full border border-red-500/40 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-red-300">No number</span>
              )}
              {d.contact?.email ? (
                <a href={`mailto:${d.contact.email}`} className="rounded-full border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300">
                  {d.contact.email}
                </a>
              ) : null}
            </div>
          </div>
        </div>

        {/* activity */}
        <div>
          <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">Activity &amp; notes</div>
          <div className="mb-2 flex flex-wrap gap-1">
            {ACT_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`pipeline-act-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  tab === t.key ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-zinc-800 text-zinc-400 hover:border-indigo-500/40"
                }`}
              >
                <t.icon size={11} weight="bold" /> {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="pipeline-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addActivity()}
              placeholder={`Log a ${tab} — what happened?`}
              className="h-9 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-xs text-zinc-100 focus:border-indigo-500 focus:outline-none"
            />
            <button
              type="button"
              data-testid="pipeline-note-add"
              onClick={addActivity}
              disabled={busy || !note.trim()}
              className="rounded-md bg-indigo-500 px-3 text-[11px] font-bold uppercase tracking-widest text-white disabled:opacity-40"
            >
              Add
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {(d.activities || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500">No activity yet — log the first note above.</p>
            ) : (
              d.activities.map((a, i) => (
                <div key={i} data-testid={`pipeline-activity-${i}`} className="rounded-md border border-zinc-800 bg-zinc-900 p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-indigo-300">{a.type}</span>
                    <span className="text-[10px] text-zinc-500">{fmtDate(a.at)}</span>
                  </div>
                  <div className="text-zinc-200">{a.text}</div>
                  {a.by ? <div className="text-[10px] text-zinc-600">{a.by}</div> : null}
                </div>
              ))
            )}
          </div>
        </div>

        {/* tags */}
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
            <Tag size={11} weight="bold" /> Tags
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {(d.tags || []).map((t) => (
              <span key={t} className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300">
                {t}
                <button type="button" onClick={() => removeTag(t)} className="text-zinc-500 hover:text-red-400">×</button>
              </span>
            ))}
            <form onSubmit={addTag}>
              <input
                data-testid="pipeline-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tag + Enter"
                className="h-7 w-32 rounded-full border border-zinc-800 bg-zinc-950 px-3 text-[11px] text-zinc-100 focus:border-indigo-500 focus:outline-none"
              />
            </form>
          </div>
        </div>

        {/* stage history */}
        <div>
          <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
            <ClockCounterClockwise size={11} weight="bold" /> Stage history
          </div>
          {(d.stage_history || []).length === 0 ? (
            <p className="text-[11px] text-zinc-500">No stage moves recorded yet.</p>
          ) : (
            <div className="space-y-1">
              {d.stage_history.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className={STAGE_COLOR[h.stage] || "text-zinc-300"}>{h.stage}</span>
                  <span className="text-zinc-500">{fmtDate(h.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* dates */}
        <div className="grid grid-cols-2 gap-3 border-t border-zinc-800 pt-3 text-[11px]">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Created</span>
            <div className="text-zinc-300">{fmtDate(d.created_at)}</div>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Last updated</span>
            <div className="text-zinc-300">{fmtDate(d.updated_at)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Pipeline() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [drag, setDrag] = useState(null);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/pipeline");
    setData(d);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openCard = async (card) => {
    const { data: d } = await api.get(`/pipeline/${card.entity}/${card.id}`);
    setDetail(d);
  };
  const reloadDetail = async () => {
    if (!detail) return;
    const { data: d } = await api.get(`/pipeline/${detail.entity}/${detail.id}`);
    setDetail(d);
    load();
  };
  const move = async (entity, id, stage) => {
    try {
      await api.post(`/pipeline/${entity}/${id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
      if (detail && detail.entity === entity && detail.id === id) {
        const { data: d } = await api.get(`/pipeline/${entity}/${id}`);
        setDetail(d);
      }
    } catch {
      toast.error("Could not move");
    }
  };

  const onDrop = (stage) => {
    setDragOver(null);
    if (drag && drag.stage !== stage) move(drag.entity, drag.id, stage);
    setDrag(null);
  };

  if (!data) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="pipeline-loading">Loading pipeline…</div>;
  }
  const s = data.stats;
  const byStage = (stage) => data.cards.filter((c) => c.stage === stage);

  return (
    <div className="flex h-full flex-col" data-testid="pipeline-page">
      {/* stats bar */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-zinc-800 bg-zinc-950 px-4 py-3">
        <div className="mr-2 flex items-center gap-2">
          <h1 className="text-base font-bold text-zinc-100">Pipeline</h1>
          <span className="text-[11px] text-zinc-500">{s.pipeline_count} open</span>
        </div>
        <Stat label="Pipeline" value={money(s.pipeline_total)} />
        <Stat label="Weighted" value={money(s.weighted)} tone="text-indigo-300" />
        <Stat label="Closing this mo." value={`${s.closing_count} · ${money(s.closing_value)}`} />
        <Stat label="Won this mo." value={`${s.won_count} · ${money(s.won_value)}`} tone="text-emerald-300" />
        <Stat label="Win rate" value={`${s.win_rate}%`} />
      </div>

      {/* board */}
      <div className="flex flex-1 gap-3 overflow-x-auto p-4" data-testid="pipeline-board">
        {data.stages.map((stage) => {
          const cards = byStage(stage);
          const total = cards.reduce((n, c) => n + c.value, 0);
          return (
            <div
              key={stage}
              data-testid={`pipeline-column-${stage.replace(/\s+/g, "-")}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(stage);
              }}
              onDragLeave={() => setDragOver((v) => (v === stage ? null : v))}
              onDrop={() => onDrop(stage)}
              className={`flex w-72 shrink-0 flex-col rounded-lg border ${
                dragOver === stage ? "border-indigo-500 bg-indigo-500/5" : "border-zinc-800 bg-zinc-900/40"
              }`}
            >
              <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
                <span className={`text-[11px] font-bold uppercase tracking-widest ${STAGE_COLOR[stage] || "text-zinc-300"}`}>
                  {stage} <span className="text-zinc-500">{cards.length}</span>
                </span>
                <span className="font-mono-data text-[11px] font-bold text-zinc-400">{money(total)}</span>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-2">
                {cards.length === 0 ? (
                  <p className="px-1 py-4 text-center text-[10px] text-zinc-600">Drop deals here</p>
                ) : (
                  cards.map((c) => (
                    <PipelineCard
                      key={`${c.entity}-${c.id}`}
                      card={c}
                      onOpen={openCard}
                      onDragStart={(e, cc) => {
                        setDrag(cc);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {detail && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setDetail(null)} data-testid="pipeline-drawer-backdrop" />
          <Drawer detail={detail} onClose={() => setDetail(null)} onMove={move} onReload={reloadDetail} />
        </>
      )}
    </div>
  );
}
